<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useOrdersStore } from '@/stores/orders'
import { actionNotice, clearActionNotice } from '@/lib/action-notice'
import IconDocSearch from '@/components/IconDocSearch.vue'

const authStore = useAuthStore()
const { isAuthenticated, profile, isAdmin } = storeToRefs(authStore)

/**
 * Open-orders badge on the Bestellingen nav button. Refreshed on every route
 * change (one cheap COUNT query) and on login, so the number is honest
 * "whenever navigating around" without a poll while the tab sits idle;
 * drawer actions bump it via the store (applyOrderUpdate).
 */
const ordersStore = useOrdersStore()
const { actionableCount } = storeToRefs(ordersStore)
const router = useRouter()
router.afterEach(() => {
  if (authStore.isAuthenticated) void ordersStore.refreshOpenCount()
})
watch(isAuthenticated, authed => {
  if (authed) void ordersStore.refreshOpenCount()
})

/**
 * The admin-only icon links. One list rather than near-identical `router-link`
 * blocks — each route's label is `nav.<name>`, so the name is the only thing
 * that actually differs besides the glyph. The audit log lives INSIDE
 * Rapportage (a tab on ReportsView) rather than as a third link here, so the
 * reports icon is a document-with-magnifier covering both meanings; `icon` is
 * either a text glyph or an icon component.
 */
const ADMIN_LINKS = [
  { name: 'reports', icon: IconDocSearch },
  { name: 'settings', icon: '⚙' },
] as const

// Mobile hamburger state. Ignored on desktop (the menu is always laid out
// inline there via CSS); on mobile it gates the dropdown panel.
const menuOpen = ref(false)
function closeMenu() {
  menuOpen.value = false
}

// Escape closes the open mobile menu — same house pattern as the move dialog
// and RecordDrawer, so keyboard users can always dismiss it.
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && menuOpen.value) closeMenu()
}

onMounted(() => {
  document.addEventListener('keydown', onKey)
  void authStore.loadFromStorage()
})
onUnmounted(() => document.removeEventListener('keydown', onKey))

function logout() {
  closeMenu()
  void authStore.logout()
}
</script>

<template>
  <header class="app-header">
    <div class="brand">
      <div class="logo-mark" aria-hidden="true" />
      <h1>Random <span class="brand-accent">Vinyl</span></h1>
    </div>

    <!-- Mobile only (hidden ≥1100px): toggles the dropdown menu. -->
    <button
      v-if="isAuthenticated"
      type="button"
      class="nav-toggle"
      :aria-label="$t('nav.menu_aria')"
      :aria-expanded="menuOpen"
      aria-controls="app-menu"
      @click="menuOpen = !menuOpen"
    >
      <span class="nav-toggle-bar" aria-hidden="true" />
      <span class="nav-toggle-bar" aria-hidden="true" />
      <span class="nav-toggle-bar" aria-hidden="true" />
    </button>

    <div v-if="isAuthenticated" id="app-menu" class="app-menu" :class="{ open: menuOpen }">
      <nav class="app-nav" :aria-label="$t('nav.main_aria')" @click="closeMenu">
        <router-link :to="{ name: 'label' }" class="nav-cta">{{
          $t('nav.new_record')
        }}</router-link>
        <router-link :to="{ name: 'catalogue' }">{{ $t('nav.catalogue') }}</router-link>
        <router-link :to="{ name: 'pos' }">{{ $t('nav.pos') }}</router-link>
        <router-link :to="{ name: 'boxes' }">{{ $t('nav.boxes') }}</router-link>
        <router-link :to="{ name: 'orders' }">
          <!-- Bold accent count BEFORE the label: the eye lands on it from
               anywhere in the app when there is work waiting. -->
          <span v-if="actionableCount" class="nav-badge">{{ actionableCount }}</span>
          {{ $t('nav.orders') }}
        </router-link>
      </nav>
      <div v-if="profile" class="app-user">
        <span class="user-email">{{ profile.email ?? profile.sub }}</span>
        <button type="button" class="btn-link" @click="logout">{{ $t('nav.logout') }}</button>
        <!-- Admin-only Rapportage (reports + audit tab) and settings: compact
             icons to the right of logout rather than full nav buttons. `v-if`
             on the wrapper, not `v-show` on each link — a non-admin gets no
             admin hrefs in the DOM at all. -->
        <template v-if="isAdmin">
          <router-link
            v-for="link in ADMIN_LINKS"
            :key="link.name"
            v-tippy="$t(`nav.${link.name}`)"
            :to="{ name: link.name }"
            class="admin-icon"
            :aria-label="$t(`nav.${link.name}`)"
            @click="closeMenu"
          >
            <span aria-hidden="true">
              <component :is="link.icon" v-if="typeof link.icon !== 'string'" />
              <template v-else>{{ link.icon }}</template>
            </span>
          </router-link>
        </template>
      </div>
    </div>
  </header>

  <!-- Transient app-level warning (e.g. a write proceeded while Discogs was
       unreachable). Auto-clears; dismissible. -->
  <div v-if="actionNotice" class="app-notice" role="status">
    <span>⚠ {{ $t(`warnings.${actionNotice}`) }}</span>
    <button
      type="button"
      class="app-notice-close"
      :aria-label="$t('common.close')"
      @click="clearActionNotice"
    >
      ✕
    </button>
  </div>

  <main class="app-main">
    <router-view />
  </main>
</template>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  padding: var(--space-3) var(--space-6);
  border-bottom: 1px solid var(--border-subtle);
}
.brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
.logo-mark {
  width: 32px;
  height: 32px;
  border: 2px solid var(--accent);
  border-radius: 50%;
  position: relative;
  flex-shrink: 0;
}
.logo-mark::after {
  content: '';
  position: absolute;
  inset: 5px;
  border: 2px solid var(--accent);
  border-radius: 50%;
}
.logo-mark::before {
  content: '';
  position: absolute;
  inset: 12px;
  background: var(--accent);
  border-radius: 50%;
}
.brand h1 {
  font-family: var(--font-family-display);
  font-size: 1.5rem;
  letter-spacing: 0.18rem;
  margin: 0;
}
.brand-accent {
  color: var(--accent);
}

/* Menu wrapper — an inline row on desktop; collapses into a dropdown panel
   on mobile (see the max-width query at the end of this block). */
.app-menu {
  display: flex;
  flex: 1;
  align-items: center;
  gap: var(--space-6);
}
.app-nav {
  display: flex;
  gap: var(--space-2);
}
/* Nav items carry button chrome so they read clearly as links to other pages. */
.app-nav a {
  color: var(--mid);
  text-decoration: none;
  font-family: var(--font-family-mono);
  font-size: 0.78rem;
  letter-spacing: 0.12rem;
  text-transform: uppercase;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--surface-card);
  white-space: nowrap;
  transition: all 0.15s;
}
.nav-badge {
  color: var(--accent);
  font-weight: 700;
  font-size: 0.85rem;
  margin-right: var(--space-1);
}
.app-nav a:hover {
  color: var(--white);
  background: var(--surface-input-hover);
}
.app-nav a.router-link-active {
  color: var(--white);
  border-color: var(--warm);
}
/* Primary call-to-action — orange, but the same footprint as the other items. */
.app-nav a.nav-cta {
  color: var(--white);
  background: var(--accent);
  border-color: var(--accent);
}
.app-nav a.nav-cta:hover {
  background: var(--accent-light);
  border-color: var(--accent-light);
}
.app-nav a.nav-cta.router-link-active {
  border-color: var(--white);
}

/* Hamburger — hidden on desktop, shown by the mobile query below. */
.nav-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-1);
  margin-left: auto;
  width: var(--tap-target-min);
  height: var(--tap-target-min);
  padding: 0 var(--space-3);
  background: var(--surface-card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  cursor: pointer;
}
.nav-toggle-bar {
  display: block;
  height: 2px;
  background: var(--white);
  border-radius: 2px;
}

.app-user {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
.user-email {
  font-size: 0.78rem;
  color: var(--mid);
}
.btn-link {
  background: none;
  border: none;
  color: var(--warm);
  cursor: pointer;
  font-family: var(--font-family-mono);
  font-size: 0.72rem;
  padding: 0;
}
.btn-link:hover {
  color: var(--white);
}
/* Compact admin-only icon links (reports, settings) — sit to the right of the
   logout link. */
.admin-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  /* The glyph (text ⚙ and em-sized SVG alike) nearly fills the 32px square —
     24px of icon, 4px of breathing room a side. */
  font-size: 1.5rem;
  line-height: 1;
  color: var(--mid);
  text-decoration: none;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--surface-card);
  transition: all 0.15s;
}
/* Kill the glyph span's inline baseline gap so the SVG centres optically. */
.admin-icon > span {
  display: grid;
  place-items: center;
}
.admin-icon:hover {
  color: var(--white);
  background: var(--surface-input-hover);
}
.admin-icon.router-link-active {
  color: var(--white);
  border-color: var(--warm);
}

.app-main {
  padding: var(--space-6);
}
.app-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin: var(--space-3) var(--space-6) 0;
  padding: var(--space-2) var(--space-3);
  font-size: 0.85rem;
  color: var(--warm);
  background: var(--accent-bg-subtle, rgba(255, 196, 0, 0.12));
  border: 1px solid var(--warm);
  border-radius: var(--radius-md);
}
.app-notice-close {
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 1rem;
}

/* Mobile (<1100px): brand stays top-left, the hamburger sits top-right, and
   the menu — nav buttons (incl. the orange "Nieuwe plaat" CTA) plus the user
   block — collapses into a full-width dropdown panel below the header row,
   revealed by toggling the hamburger. */
@media (max-width: 1099.98px) {
  .app-header {
    flex-wrap: wrap;
    gap: var(--space-4);
  }
  .nav-toggle {
    display: inline-flex;
  }
  .app-menu {
    display: none;
    order: 3;
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-3);
    width: 100%;
    padding-top: var(--space-3);
    border-top: 1px solid var(--border-subtle);
  }
  .app-menu.open {
    display: flex;
  }
  .app-nav {
    flex-direction: column;
    gap: var(--space-2);
  }
  .app-nav a {
    text-align: center;
  }
  .app-user {
    margin-left: 0;
    justify-content: space-between;
  }
}
</style>
